import React, { useEffect, useState, useCallback, useRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import { ArrowRight, X, Sparkles, MousePointerClick, Rocket } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

/* ------------------------------------------------------------------ */
/*  LaunchAnnouncement — pre-launch preview popup                       */
/*                                                                      */
/*  Shows once per browser session (sessionStorage) the first time a    */
/*  visitor opens the site, explaining that the data is sample/demo     */
/*  content while the build is being finished. Bilingual (sv default).  */
/*  Radix Dialog for focus trap, ESC + overlay dismiss, and a11y.       */
/* ------------------------------------------------------------------ */

const SESSION_KEY = 'hjalptorget:launch-notice-seen'

function alreadySeen(): boolean {
  try {
    return window.sessionStorage?.getItem(SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function markSeen(): void {
  try {
    window.sessionStorage?.setItem(SESSION_KEY, '1')
  } catch {
    /* sessionStorage unavailable — modal simply re-appears next load */
  }
}

const HIGHLIGHT_ICONS: ReadonlyArray<React.ReactNode> = [
  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />,
  <MousePointerClick className="h-3.5 w-3.5" aria-hidden="true" />,
  <Rocket className="h-3.5 w-3.5" aria-hidden="true" />,
]

const COPY = {
  sv: {
    close: 'Stäng',
    badge: 'Förhandsvisning före lansering',
    title: 'En första titt, innan vi lanserar.',
    body:
      'Vi lägger sista handen vid Hjälptorget. Just nu är allt du ser — leverantörer, omdömen, bokningar och priser — exempeldata som visar hur marknadsplatsen fungerar. Utforska fritt, inget här är en riktig bokning.',
    highlights: ['Exempeldata', 'Fullt klickbar', 'Lanseras snart'],
    cta: 'Utforska demon',
    finePrint: 'Ingen registrering krävs. Du ser inte det här igen under den här sessionen.',
  },
  en: {
    close: 'Close',
    badge: 'Pre-launch preview',
    title: 'A first look, before we launch.',
    body:
      "We're putting the finishing touches on Hjälptorget. For now, everything you see, the providers, reviews, bookings and prices, is sample data that shows how the marketplace works. Explore freely, nothing here is a real booking.",
    highlights: ['Sample data', 'Fully clickable', 'Launching soon'],
    cta: 'Explore the demo',
    finePrint: "No sign-up needed. You won't see this again this session.",
  },
} as const

export function LaunchAnnouncement() {
  const { i18n } = useTranslation()
  const c = i18n.language === 'en' ? COPY.en : COPY.sv

  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState(false)

  // Appear a moment after load so the popup settles in rather than popping in instantly.
  useEffect(() => {
    if (alreadySeen()) return
    const t = window.setTimeout(() => setOpen(true), 600)
    return () => window.clearTimeout(t)
  }, [])

  // Run the enter transition on the frame after the dialog mounts.
  useEffect(() => {
    if (!open) return
    const r = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(r)
  }, [open])

  const closeTimeoutRef = useRef<number | null>(null)

  const dismiss = useCallback(() => {
    markSeen()
    setVisible(false)
    closeTimeoutRef.current = window.setTimeout(() => setOpen(false), 280)
  }, [])

  // Clear any pending close timeout if the component unmounts mid-transition.
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) window.clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) dismiss()
    },
    [dismiss],
  )

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm',
            'transition-opacity duration-500 ease-out motion-reduce:transition-none',
            visible ? 'opacity-100' : 'opacity-0',
          )}
        />
        <DialogPrimitive.Content
          aria-describedby="launch-desc"
          className={cn(
            'fixed left-1/2 top-1/2 z-[60] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2',
            'overflow-hidden rounded-3xl border border-white/10 shadow-2xl outline-none',
            'transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform',
            'motion-reduce:transition-none',
            visible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.96]',
          )}
          style={{
            background:
              'linear-gradient(157deg, rgb(17 31 24) 0%, rgb(11 15 19) 52%, rgb(14 27 21) 100%)',
          }}
        >
          {/* Decorative glow + grain */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(120% 80% at 88% -10%, rgba(120,224,168,0.18) 0%, rgba(120,224,168,0) 55%), radial-gradient(90% 70% at -10% 110%, rgba(59,124,82,0.22) 0%, rgba(59,124,82,0) 60%)',
            }}
          />
          {/* Top accent hairline */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, rgba(143,227,176,0) 0%, rgba(143,227,176,0.7) 50%, rgba(143,227,176,0) 100%)',
            }}
          />

          {/* Close */}
          <DialogPrimitive.Close
            aria-label={c.close}
            className={cn(
              'absolute right-4 top-4 z-10 rounded-full p-1.5 text-white/50',
              'transition-colors duration-150 hover:bg-white/10 hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FE3B0]/70',
            )}
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          <div className="relative px-7 pb-8 pt-8 sm:px-10 sm:pt-10">
            {/* Badge */}
            <span className="inline-flex items-center gap-2 rounded-full border border-[#8FE3B0]/25 bg-[#8FE3B0]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8FE3B0]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#8FE3B0] opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#8FE3B0]" />
              </span>
              {c.badge}
            </span>

            {/* Brand */}
            <div className="mt-6 flex items-center gap-2.5">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-[10px] font-display text-lg font-bold text-white shadow-sm"
                style={{ backgroundColor: 'rgb(59 124 82)' }}
                aria-hidden="true"
              >
                H
              </span>
              <span className="font-display text-lg font-bold tracking-tight text-white">
                Hjälptorget
              </span>
            </div>

            {/* Headline */}
            <DialogPrimitive.Title className="mt-5 font-display text-[1.9rem] font-bold leading-[1.12] text-white sm:text-[2.15rem]">
              {c.title}
            </DialogPrimitive.Title>

            {/* Body */}
            <p id="launch-desc" className="mt-3.5 text-[15px] leading-relaxed text-white/70">
              {c.body}
            </p>

            {/* Highlights */}
            <div className="mt-6 flex flex-wrap gap-2">
              {c.highlights.map((label, idx) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-white/75"
                >
                  <span className="text-[#8FE3B0]">{HIGHLIGHT_ICONS[idx]}</span>
                  {label}
                </span>
              ))}
            </div>

            {/* CTA */}
            <button
              type="button"
              onClick={dismiss}
              className={cn(
                'group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5',
                'text-sm font-semibold text-white shadow-lg transition-all duration-200',
                'hover:-translate-y-0.5 hover:shadow-xl',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FE3B0] focus-visible:ring-offset-2',
                'focus-visible:ring-offset-[rgb(14_27_21)]',
              )}
              style={{ backgroundColor: 'rgb(59 124 82)' }}
            >
              {c.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>

            {/* Fine print */}
            <p className="mt-3.5 text-center text-xs text-white/40">
              {c.finePrint}
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

LaunchAnnouncement.displayName = 'LaunchAnnouncement'
